/**
 * Select Component - Phase 3H-A
 * Dropdown select input - compatible with existing code patterns
 * 
 * Fixed: Dark theme styling for admin pages
 */
import { SelectHTMLAttributes, forwardRef, ChangeEvent } from 'react';

interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size' | 'onChange'> {
  label?: string;
  error?: string;
  options: SelectOption[];
  size?: 'sm' | 'md' | 'lg';
  /** onChange receives the new value string directly (not the event) */
  onChange?: (value: string) => void;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, error, options, size = 'md', className = '', onChange, ...props }, ref) => {
    const sizes = {
      sm: 'px-2 py-1 text-sm',
      md: 'px-3 py-2 text-base',
      lg: 'px-4 py-3 text-lg',
    };

    const handleChange = (e: ChangeEvent<HTMLSelectElement>) => {
      onChange?.(e.target.value);
    };

    return (
      <div className="w-full">
        {label && (
          <label className="block text-sm font-medium text-slate-300 mb-1">
            {label}
          </label>
        )}
        <select
          ref={ref}
          onChange={handleChange}
          className={`
            block w-full rounded-md shadow-sm
            bg-slate-800 text-white border-slate-600
            focus:border-blue-500 focus:ring-blue-500 focus:ring-1
            disabled:bg-slate-900 disabled:cursor-not-allowed disabled:text-slate-500
            ${error ? 'border-red-500' : 'border-slate-600'}
            ${sizes[size]}
            ${className}
          `}
          {...props}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value} className="bg-slate-800 text-white">
              {option.label}
            </option>
          ))}
        </select>
        {error && (
          <p className="mt-1 text-sm text-red-400">{error}</p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';
